import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { finalize, Observable, tap } from 'rxjs';
import { UserProfileApiService } from '../../../core/services/user-profile/user-profile-api.service';
import { UserProfile } from '../../../core/interfaces/user-profile/user-profile.interface';
import {
  ClearUserProfileAction,
  LoadUserProfileAction,
  UpdateUserProfileAction
} from './user-profile.actions';

export interface UserProfileStateModel {
  profile: UserProfile | null;
  isLoading: boolean;
}

const defaults: UserProfileStateModel = {
  profile: null,
  isLoading: false
};

@State<UserProfileStateModel>({
  name: 'userProfile',
  defaults
})
@Injectable()
export class UserProfileState {
  constructor(private userProfileApi: UserProfileApiService) {}

  @Selector()
  static profile(state: UserProfileStateModel): UserProfile | null {
    return state.profile;
  }

  @Selector()
  static isLoading(state: UserProfileStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static displayName(state: UserProfileStateModel): string {
    const p = state.profile;
    if (!p) return '';
    return `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
  }

  @Selector()
  static avatarUrl(state: UserProfileStateModel): string {
    return state.profile?.avatar_url ?? '';
  }

  @Action(LoadUserProfileAction)
  loadProfile(ctx: StateContext<UserProfileStateModel>): Observable<any> {
    ctx.patchState({ isLoading: true });

    return this.userProfileApi.getProfile().pipe(
      tap((profile) => {
        if (profile?.id) {
          ctx.patchState({ profile });
        }
      }),
      finalize(() => ctx.patchState({ isLoading: false }))
    );
  }

  @Action(UpdateUserProfileAction)
  updateProfile(
    ctx: StateContext<UserProfileStateModel>,
    { payload }: UpdateUserProfileAction
  ): Observable<any> {
    return this.userProfileApi.updateProfile(payload).pipe(
      tap((profile) => {
        if (profile?.id) {
          ctx.patchState({ profile });
        }
      })
    );
  }

  @Action(ClearUserProfileAction)
  clearProfile(ctx: StateContext<UserProfileStateModel>): void {
    ctx.setState(defaults);
  }
}
